import { useState } from 'react';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EditIcon from '@mui/icons-material/Edit';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import config from '../../config';
import { createPaymentIntent } from '../../REST/payment';

const stripePromise = config.REACT_APP_STRIPE_PK ? loadStripe(config.REACT_APP_STRIPE_PK) : null;

const ELEMENT_STYLE = {
    style: {
        base: {
            fontSize: '18px',
            lineHeight: '28px',
            '::placeholder': { color: '#9aa0a6' }
        }
    }
};

// Drop-in replacement for PayCard (Square) — same amount/onComplete contract, backed by Stripe.
export function PayCardStripe({ amount = 0, invoiceId, lineItemUids = '', onComplete }) {
    if (!stripePromise) {
        return <Alert severity="warning">Stripe is not configured (REACT_APP_STRIPE_PK missing).</Alert>
    }

    return (
        <Elements stripe={stripePromise}>
            <StripeCardForm amount={amount} invoiceId={invoiceId} lineItemUids={lineItemUids} onComplete={onComplete} />
        </Elements>
    )
}

function StripeCardForm({ amount, invoiceId, lineItemUids = '', onComplete }) {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);

    const handlePay = async () => {
        if (!stripe || !elements) return;
        setProcessing(true);
        setError(null);

        try {
            const { data } = await createPaymentIntent({ invoiceId, amount, lineItemUids });
            const result = await stripe.confirmCardPayment(data.clientSecret, {
                payment_method: { card: elements.getElement(CardNumberElement) }
            });

            if (result.error) {
                setError(result.error.message);
                setProcessing(false);
                return;
            }

            const paymentIntent = result.paymentIntent;
            if (typeof onComplete === 'function') {
                onComplete({
                    confirmation: paymentIntent.id,
                    accountDetails: 'Stripe',
                    accountName: 'Card',
                    paymentDate: new Date().toISOString()
                });
            }
        } catch (ex) {
            setError(ex.response?.data?.message || ex.message);
        } finally {
            setProcessing(false);
        }
    }

    const fieldSx = { border: '1px solid #ccc', borderRadius: 1, px: 1.5, py: 1.25 };

    return (
        <div className='row'>
            <div className='col-12 bg-white'>
                {error && <Alert severity="error">{error}</Alert>}

                <Box sx={{ mt: 1.5, mb: 1 }}>
                    <Typography variant='caption' color='text.secondary'>Card Number</Typography>
                    <Box sx={fieldSx}>
                        <CardNumberElement options={ELEMENT_STYLE} />
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant='caption' color='text.secondary'>Expiry</Typography>
                        <Box sx={fieldSx}>
                            <CardExpiryElement options={ELEMENT_STYLE} />
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant='caption' color='text.secondary'>CVC</Typography>
                        <Box sx={fieldSx}>
                            <CardCvcElement options={ELEMENT_STYLE} />
                        </Box>
                    </Box>
                </Box>

                <Button
                    color='primary'
                    fullWidth
                    size='large'
                    variant='contained'
                    disabled={!stripe || processing}
                    onClick={handlePay}
                    sx={{ py: 1.5, fontSize: '1.1rem' }}
                >
                    <b>{processing ? 'Processing...' : `Pay With Card $${Number(amount).toFixed(2)}`}</b>
                </Button>
            </div>
        </div>
    )
}

export function PayAchStripe({ amount = 0, invoiceId, lineItemUids = '', payerEmail = '', onComplete }) {
    if (!stripePromise) {
        return <Alert severity="warning">Stripe is not configured (REACT_APP_STRIPE_PK missing).</Alert>
    }

    return (
        <Elements stripe={stripePromise}>
            <StripeAchForm amount={amount} invoiceId={invoiceId} lineItemUids={lineItemUids} payerEmail={payerEmail} onComplete={onComplete} />
        </Elements>
    )
}

function StripeAchForm({ amount, invoiceId, lineItemUids = '', payerEmail = '', onComplete }) {
    const stripe = useStripe();
    const [tab, setTab] = useState(0); // 0 = Instant (Financial Connections), 1 = Manual Entry
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState(payerEmail || '');
    const [routingNum, setRoutingNum] = useState('');
    const [accountNum, setAccountNum] = useState('');
    const [accountType, setAccountType] = useState('individual');

    // Flow 1: Instant Bank Login (Stripe Financial Connections)
    const handleInstantPay = async () => {
        if (!stripe) return;
        if (!name.trim()) {
            setError('Please provide the Account Holder Name.');
            return;
        }
        setProcessing(true);
        setError(null);

        try {
            const { data } = await createPaymentIntent({ invoiceId, amount, lineItemUids });

            // Launch Stripe Financial Connections modal to collect and link bank account
            const collectResult = await stripe.collectBankAccountForPayment({
                clientSecret: data.clientSecret,
                params: {
                    payment_method_type: 'us_bank_account',
                    payment_method_data: {
                        billing_details: {
                            name: name.trim(),
                            email: (email || payerEmail || 'tenant@example.com').trim()
                        }
                    }
                }
            });

            if (collectResult.error) {
                setError(collectResult.error.message);
                setProcessing(false);
                return;
            }

            // Confirm payment with the linked bank account
            const confirmResult = await stripe.confirmUsBankAccountPayment(data.clientSecret);
            if (confirmResult.error) {
                setError(confirmResult.error.message);
                setProcessing(false);
                return;
            }

            const paymentIntent = confirmResult.paymentIntent;
            if (typeof onComplete === 'function') {
                onComplete({
                    confirmation: paymentIntent.id,
                    accountDetails: 'Stripe ACH (Instant)',
                    accountName: 'ACH',
                    paymentDate: new Date().toISOString()
                });
            }
        } catch (ex) {
            setError(ex.response?.data?.message || ex.message);
        } finally {
            setProcessing(false);
        }
    };

    // Flow 2: Manual Routing & Account Number Entry
    const handleManualPay = async () => {
        if (!stripe) return;
        if (!name || !routingNum || !accountNum) {
            setError('Please fill in all bank details.');
            return;
        }
        setProcessing(true);
        setError(null);

        try {
            const { data } = await createPaymentIntent({ invoiceId, amount, lineItemUids });

            const result = await stripe.confirmUsBankAccountPayment(data.clientSecret, {
                payment_method: {
                    billing_details: {
                        name: name.trim(),
                        email: (email || payerEmail || 'tenant@example.com').trim()
                    },
                    us_bank_account: {
                        routing_number: routingNum.trim(),
                        account_number: accountNum.trim(),
                        account_holder_type: accountType
                    }
                }
            });

            if (result.error) {
                setError(result.error.message);
                setProcessing(false);
                return;
            }

            const paymentIntent = result.paymentIntent;
            if (typeof onComplete === 'function') {
                onComplete({
                    confirmation: paymentIntent.id,
                    accountDetails: 'Stripe ACH (Manual)',
                    accountName: 'ACH',
                    paymentDate: new Date().toISOString()
                });
            }
        } catch (ex) {
            setError(ex.response?.data?.message || ex.message);
        } finally {
            setProcessing(false);
        }
    };

    const inputSx = { mb: 2 };

    return (
        <div className='row'>
            <div className='col-12 bg-white' style={{ marginTop: '16px' }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
                    <Tabs
                        value={tab}
                        onChange={(e, val) => { setTab(val); setError(null); }}
                        variant="fullWidth"
                    >
                        <Tab
                            icon={<AccountBalanceIcon fontSize="small" />}
                            iconPosition="start"
                            label="Instant Bank Login"
                        />
                        <Tab
                            icon={<EditIcon fontSize="small" />}
                            iconPosition="start"
                            label="Manual Entry"
                        />
                    </Tabs>
                </Box>

                {/* Tab 0: Financial Connections (Instant) */}
                {tab === 0 && (
                    <Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Log in directly to your bank account via Stripe to authenticate instantly without waiting for trial deposits.
                        </Alert>

                        <TextField
                            fullWidth
                            label="Account Holder Name"
                            size="small"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            sx={inputSx}
                        />

                        <TextField
                            fullWidth
                            label="Billing Email"
                            size="small"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            sx={inputSx}
                        />

                        <Button
                            color='primary'
                            fullWidth
                            size='large'
                            variant='contained'
                            startIcon={<AccountBalanceIcon />}
                            disabled={!stripe || processing}
                            onClick={handleInstantPay}
                            sx={{ py: 1.5, fontSize: '1.05rem', mt: 1 }}
                        >
                            <b>{processing ? 'Connecting to Bank...' : `Connect Bank & Pay $${Number(amount).toFixed(2)}`}</b>
                        </Button>
                    </Box>
                )}

                {/* Tab 1: Manual Routing & Account Entry */}
                {tab === 1 && (
                    <Box>
                        <TextField
                            fullWidth
                            label="Account Holder Name"
                            size="small"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            sx={inputSx}
                        />

                        <TextField
                            fullWidth
                            label="Routing Number"
                            size="small"
                            value={routingNum}
                            onChange={e => setRoutingNum(e.target.value)}
                            sx={inputSx}
                        />

                        <TextField
                            fullWidth
                            label="Account Number"
                            size="small"
                            value={accountNum}
                            onChange={e => setAccountNum(e.target.value)}
                            sx={inputSx}
                        />

                        <TextField
                            fullWidth
                            select
                            label="Account Type"
                            size="small"
                            value={accountType}
                            onChange={e => setAccountType(e.target.value)}
                            SelectProps={{ native: true }}
                            sx={inputSx}
                        >
                            <option value="individual">Individual</option>
                            <option value="company">Company</option>
                        </TextField>

                        <Button
                            color='primary'
                            fullWidth
                            size='large'
                            variant='contained'
                            disabled={!stripe || processing}
                            onClick={handleManualPay}
                            sx={{ py: 1.5, fontSize: '1.1rem', mt: 1 }}
                        >
                            <b>{processing ? 'Processing...' : `Submit ACH $${Number(amount).toFixed(2)}`}</b>
                        </Button>
                    </Box>
                )}
            </div>
        </div>
    )
}
